
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * AetTimezone
 *
 * @file AetTimezone.jsx
 * @author Anuja Gaikwad
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
  Select,
  MenuItem,
  FormControl,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';
import CommonStyles from '../../assets/styles/CommonStyles';
import {
  quickScheduleFormFieldLabelStyles,
  QUICK_SCHEDULE_FORM_TIMEZONE_FIELD_CLASS
} from '../QuickSchedule/quickScheduleFormConstants';
import { ASSIGNMENT_FORM_TIMEZONE_FIELD_CLASS } from '../../common/constants';

const QUICK_SCHEDULE_FORM_TIMEZONE_FIELD = `.${QUICK_SCHEDULE_FORM_TIMEZONE_FIELD_CLASS}`;
const ASSIGNMENT_FORM_TIMEZONE_FIELD = `.${ASSIGNMENT_FORM_TIMEZONE_FIELD_CLASS}`;

const assignmentFormFieldLabelStyles = {
  fontWeight: mixins.fontWeight500,
  fontSize: mixins.fontSize16,
  color: mixins.lightTextColor2,
  lineHeight: '20px',
  paddingBottom: 4
};

const Wrapper = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    minWidth: 0
  }
});

const Label = styled(Typography)({
  '&&': {
    fontSize: 14,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.greyScaleColor,
    lineHeight: '22px',
    whiteSpace: 'nowrap'
  },
  [`${QUICK_SCHEDULE_FORM_TIMEZONE_FIELD} &&`]: quickScheduleFormFieldLabelStyles(mixins),
  [`${ASSIGNMENT_FORM_TIMEZONE_FIELD} &&`]: assignmentFormFieldLabelStyles
});

const StyledFormControl = styled(FormControl)({
  '&&': {
    width: '100%'
  }
});

const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'hasSuccess'
})({
  '&&': {
    height: 48,
    borderRadius: 4,
    backgroundColor: mixins.chalkWhiteFont,
    cursor: 'pointer',

    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      boxSizing: 'border-box',
      height: 48,
      minHeight: 48,
      padding: '12px !important',
      paddingRight: '12px !important',
      flex: 1,
      minWidth: 0,
      cursor: 'pointer',
      fontSize: mixins.fontSize16,
      fontWeight: mixins.fontWeight400,
      fontFamily: mixins.fontFamilyPlusSans,
      color: mixins.greyScaleColor,
      lineHeight: '24px'
    },
    '& .MuiSelect-icon': {
      display: 'none'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethyst
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethyst
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethyst,
      borderWidth: 1
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: mixins.chalkWhiteFont
    }
  },
  [`${QUICK_SCHEDULE_FORM_TIMEZONE_FIELD} &&`]: {
    boxShadow: 'none',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethystColorV2
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethystColorV2
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethystColorV2,
      borderWidth: 1
    },
    '& .MuiSelect-select': {
      fontSize: mixins.fontSize16,
      color: '#2E2E2E',
      lineHeight: '24px'
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: mixins.chalkWhiteFont,
      outline: 'none'
    }
  },
  [`${ASSIGNMENT_FORM_TIMEZONE_FIELD} &&`]: {
    boxShadow: 'none',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethyst
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethyst
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: mixins.amethystColorV2,
      borderWidth: 1
    },
    '& .MuiSelect-select': {
      fontSize: mixins.fontSize16,
      color: mixins.greyScaleColor,
      lineHeight: '24px'
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: mixins.chalkWhiteFont,
      outline: 'none'
    }
  }
});

const SelectValueRow = styled('span')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minWidth: 0,
    gap: 8
  }
});

const SelectValueText = styled('span')({
  '&&': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0
  }
});

const SelectValueIcon = styled('span')({
  '&&': {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
    '& .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18,
      color: mixins.timeIconColor
    }
  },
  [`${QUICK_SCHEDULE_FORM_TIMEZONE_FIELD} &&`]: {
    '& svg': {
      fontSize: 16,
      width: 16,
      height: 16
    }
  },
  [`${ASSIGNMENT_FORM_TIMEZONE_FIELD} &&`]: {
    '& svg': {
      fontSize: 18,
      width: 18,
      height: 18
    }
  }
});

const MenuPaper = styled('div')({
  '&&': {
    ...CommonStyles().scrollContainer({
      scrollbarWidth: 8,
      scrollbarColor: 'rgba(81, 46, 171, 0.15) transparent',
      borderRadius: 4
    })
  }
});

const ThemedMenuPaper = styled(MenuPaper)({
  '&&': {
    boxShadow: mixins.paperElevation8BoxShadow,
    '--Paper-shadow': mixins.paperElevation8BoxShadow,
    '& .MuiMenuItem-root': {
      '&:hover': {
        backgroundColor: mixins.quaternary
      },
      '&.Mui-selected': {
        backgroundColor: mixins.quaternary
      },
      '&.Mui-selected:hover': {
        backgroundColor: mixins.quaternary
      }
    }
  }
});

const StyledMenuItem = styled(MenuItem)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: 10,
    whiteSpace: 'normal'
  }
});

const MenuItemName = styled('span')({
  '&&': {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.timeIconColor,
    lineHeight: '24px',
    wordBreak: 'break-word'
  }
});

const MenuItemOffset = styled('span')({
  '&&': {
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    color: mixins.timeIconColor,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '24px'
  }
});

const AetTimezone = ({
  label,
  value,
  onChange,
  icons,
  options,
  className,
  hasError,
  hasSuccess
}) => {
  const isThemedMenuField = className?.includes(QUICK_SCHEDULE_FORM_TIMEZONE_FIELD_CLASS)
    || className?.includes(ASSIGNMENT_FORM_TIMEZONE_FIELD_CLASS);

  return (
    <Wrapper className={className || ''}>
      {label && (
      <Label>
        {label}
      </Label>
      )}

      <StyledFormControl
        variant="outlined"
        error={hasError}
      >
        <StyledSelect
          value={value}
          onChange={(e) => onChange(e.target.value)}
          hasError={hasError}
          hasSuccess={hasSuccess}
          IconComponent={() => null}
          renderValue={(selected) => {
            const selectedVal = options.find(o => o.value === selected);
            const text = selectedVal ? `${selectedVal.abbr} (${selectedVal.offset})` : selected;

            if (!icons?.timeZone) return text;

            return (
              <SelectValueRow>
                <SelectValueText>{text}</SelectValueText>
                <SelectValueIcon aria-hidden>
                  {icons.timeZone}
                </SelectValueIcon>
              </SelectValueRow>
            );
          }}
          MenuProps={{
            slotProps: {
              popover: {
                sx: { marginTop: '8px', cursor: 'default' }
              }
            },
            PaperProps: {
              component: isThemedMenuField ? ThemedMenuPaper : MenuPaper,
              style: {
                width: 'var(--timezone-field-width, 216px)',
                maxHeight: '55vh'
              }
            },
            MenuListProps: {
              style: {
                paddingTop: 0,
                paddingBottom: 0
              }
            },
            TransitionProps: {
              onEntering: (element) => {
                const selectedItem = element.querySelector(
                  '[aria-selected="true"]'
                );

                if (selectedItem) {
                  selectedItem.scrollIntoView({
                    block: 'center'
                  });
                }
              }
            },
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            disablePortal: true
          }}
        >
          {options.map((item) => (
            <StyledMenuItem key={item.value} value={item.value}>
              <MenuItemName>{item.name}</MenuItemName>
              <MenuItemOffset>{item.offset}</MenuItemOffset>
            </StyledMenuItem>
          ))}
        </StyledSelect>
      </StyledFormControl>
    </Wrapper>
  );
};

AetTimezone.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  icons: PropTypes.oneOfType([PropTypes.object, PropTypes.node]),
  options: PropTypes.array.isRequired,
  className: PropTypes.string,
  hasError: PropTypes.bool,
  hasSuccess: PropTypes.bool
};

AetTimezone.defaultProps = {
  label: '',
  icons: null,
  className: '',
  hasError: false,
  hasSuccess: false
};

export default AetTimezone;
